import { Injectable } from '@angular/core';
import { RequestForTicketingReportSearch, TicketingReportSearchBookingCustomField } from "@afidium/ts-facade";
import { ITicketingSearchParams } from "app_sasha/pages/ticketing-report/ticketing-report-page.component";
import { IApiSorting, SortKey } from "./ticketing-report-domain.service";
import { SortDirection } from "libraries/interfaces/ISort";
import { IPaginationRecordset } from "libraries/component/pagination/IPagination";

@Injectable({
    providedIn: 'root'
})
export class QueryParamsMappingService {

    public mapSearchToQueryParams(ticketingSearch: RequestForTicketingReportSearch, apiSorting: IApiSorting): ITicketingSearchParams {

        let bokCFN: string[] = null;
        let bokCFV: string[] = null;

        if (ticketingSearch.booking?.customFieldArray?.length) {
            ticketingSearch.booking.customFieldArray.forEach(
                (item: TicketingReportSearchBookingCustomField, index: number): void => {
                    if (!bokCFN) {
                        bokCFN = [item.name];
                        bokCFV = [item.value];
                    } else {
                        bokCFN.push(item.name);
                        bokCFV.push(item.value);
                    }

                }
            );
        }

        return {
            free: ticketingSearch.freeSearchArray,
            actTyp: ticketingSearch.action?.typeArray,
            actEveMin: ticketingSearch.action?.event?.eventMin,
            actEveMax: ticketingSearch.action?.event?.eventMax,
            actAut: ticketingSearch.action?.authorArray,
            bokRef: ticketingSearch.booking?.referenceArray,
            bokCreMin: ticketingSearch.booking?.creation?.creationMin,
            bokCreMax: ticketingSearch.booking?.creation?.creationMax,
            bokBegMin: ticketingSearch.booking?.begin?.beginMin,
            bokBegMax: ticketingSearch.booking?.begin?.beginMax,
            ticSys: ticketingSearch.ticket?.systemArray,
            ticVen: ticketingSearch?.ticket?.vendorArray,
            ticTyp: ticketingSearch.ticket?.typeArray,
            ticNum: ticketingSearch.ticket?.numberArray,
            ticAgc: ticketingSearch.ticket?.agencyArray,
            ticAgt: ticketingSearch.ticket?.agentArray,
            ticTrav: ticketingSearch.ticket?.travellerArray,
            ticAmtMin: ticketingSearch.ticket?.amount?.amountMin,
            ticAmtMax: ticketingSearch.ticket?.amount?.amountMax,
            ticComMin: ticketingSearch.ticket?.commission?.commissionMin,
            ticComMax: ticketingSearch.ticket?.commission?.commissionMax,
            ticEmiMin: ticketingSearch.ticket?.emission?.emissionMin,
            ticEmiMax: ticketingSearch.ticket?.emission?.emissionMax,
            ticPay: ticketingSearch.ticket?.paymentArray,
            bokCFV,
            bokCFN,
            sortKey: apiSorting.fieldKey,
            sortDirection: apiSorting.order,
            searchTimestamp: new Date().getTime(),
        };
    }

    public mapQueryParamsToSearch(params: ITicketingSearchParams): RequestForTicketingReportSearch {
        const bokCFN: string[] = this.getStringArray(params.bokCFN);
        const bokCFV: string[] = this.getStringArray(params.bokCFV);

        const customFieldArray: TicketingReportSearchBookingCustomField[] = bokCFN?.length ?
            bokCFN.map(
                (name: string, index: number): TicketingReportSearchBookingCustomField => {
                    return {
                        name,
                        value: bokCFV[index],
                    };
                }
            ) : null;


        const sortingArrayDate: IApiSorting = {
            fieldKey: SortKey.eventDate,
            order: SortDirection.descending,
        };
        let sortingArray: IApiSorting[] = [];

        //avoid 2 sort with Date
        if (params.sortKey === SortKey.eventDate) {
            sortingArray = [{
                fieldKey: params.sortKey,
                order: params.sortDirection,
            }];
        } else {
            //always eventDate sort first
            sortingArray = [
                {
                    fieldKey: params.sortKey,
                    order: params.sortDirection,
                },
                sortingArrayDate
            ];
        }

        const request: RequestForTicketingReportSearch = {
            //@ts-ignore
            //TODO JSV : only to be able to compile in jenkins
            //while finding a real solution
            sortingArray,
            freeSearchArray: this.getStringArray(params.free),
            action: {
                typeArray: this.getStringArray(params.actTyp),
                event: {
                    eventMin: params.actEveMin,
                    eventMax: params.actEveMax,
                },
                authorArray: this.getStringArray(params.actAut),
            },
            booking: {
                referenceArray: this.getStringArray(params.bokRef),
                creation: {
                    creationMin: params.bokCreMin,
                    creationMax: params.bokCreMax,
                },
                begin: {
                    beginMin: params.bokBegMin,
                    beginMax: params.bokBegMax,
                },
                customFieldArray,
            },
            ticket: {
                systemArray: this.getStringArray(params.ticSys),
                vendorArray: this.getStringArray(params.ticVen),
                typeArray: this.getStringArray(params.ticTyp),
                numberArray: this.getStringArray(params.ticNum),
                agencyArray: this.getStringArray(params.ticAgc),
                agentArray: this.getStringArray(params.ticAgt),
                travellerArray: this.getStringArray(params.ticTrav),
                amount: {
                    amountMin: params.ticAmtMin ? Number(params.ticAmtMin) : null,
                    amountMax: params.ticAmtMax ? Number(params.ticAmtMax) : null,
                },
                commission: {
                    commissionMin: params.ticComMin ? Number(params.ticComMin) : null,
                    commissionMax: params.ticComMax ? Number(params.ticComMax) : null,
                },
                emission: {
                    emissionMin: params.ticEmiMin,
                    emissionMax: params.ticEmiMax,
                },
                paymentArray: this.getStringArray(params.ticPay),
            }
        };

        return this.removeEmpty(request);
    }

    private getStringArray(input: string | string[] | null): string[] | null {
        if (input && Array.isArray(input)) {
            return input;
        }
        return input && typeof input === 'string' ?
            [input] as string[] :
            null;
    }

    private removeEmpty(object: RequestForTicketingReportSearch): RequestForTicketingReportSearch {
        Object.keys(object).forEach((key: string): void => {
            // If Object recall function, keep in mind : Array is Object in JS
            if (object[key] && typeof object[key] === 'object') {
                // Recursion
                // @ts-ignore
                this.removeEmpty(object[key]);

                // If object is empty then delete it
                if (!Object.keys(object[key]).length) {
                    // tslint:disable-next-line:no-dynamic-delete
                    delete object[key];
                }
            }
            // If null delete property
            else if (object[key] === null || object[key] === '' || object[key] === ' ' || object[key] === undefined) {
                // tslint:disable-next-line:no-dynamic-delete
                delete object[key];
            }
        });
        return object;
    }
}
